// Maps the 7 standard FMS tests onto simplified body regions so a single
// assessment can drive an at-a-glance body diagram for the trainer. Custom
// (trainer-defined) tests aren't mapped anywhere - there's no way to know
// what body part a freeform test targets, so they're simply left out of the
// diagram and still shown in the normal test list.
export const REGION_TEST_MAP = {
  shoulder_left: ['shoulder_mobility', 'trunk_stability_push_up'],
  shoulder_right: ['shoulder_mobility', 'trunk_stability_push_up'],
  trunk: ['rotary_stability', 'trunk_stability_push_up', 'deep_squat'],
  hip_left: ['hurdle_step', 'inline_lunge', 'active_straight_leg_raise', 'rotary_stability', 'deep_squat'],
  hip_right: ['hurdle_step', 'inline_lunge', 'active_straight_leg_raise', 'rotary_stability', 'deep_squat'],
  knee_left: ['hurdle_step', 'inline_lunge', 'deep_squat'],
  knee_right: ['hurdle_step', 'inline_lunge', 'deep_squat'],
  ankle_left: ['hurdle_step', 'inline_lunge', 'deep_squat'],
  ankle_right: ['hurdle_step', 'inline_lunge', 'deep_squat'],
};

export const REGION_LABELS = {
  shoulder_left: 'Bark (L)',
  shoulder_right: 'Bark (P)',
  trunk: 'Tułów / Core',
  hip_left: 'Biodro (L)',
  hip_right: 'Biodro (P)',
  knee_left: 'Kolano (L)',
  knee_right: 'Kolano (P)',
  ankle_left: 'Kostka / Łydka (L)',
  ankle_right: 'Kostka / Łydka (P)',
};

// Same 3-tier + unknown scheme already used for overall FMS risk (RISK_COLORS
// in FMSPage.js), applied per body region instead of to the whole assessment.
export const REGION_STATUS_COLORS = {
  high: { fill: '#ef4444', stroke: '#ef4444', label: 'WYMAGA UWAGI', text: 'text-red-500' },
  moderate: { fill: '#eab308', stroke: '#eab308', label: 'DO OBSERWACJI', text: 'text-yellow-500' },
  low: { fill: '#22c55e', stroke: '#22c55e', label: 'W PORZĄDKU', text: 'text-green-500' },
  unknown: { fill: '#2A2A2A', stroke: '#3f3f46', label: 'BRAK DANYCH', text: 'text-[#A1A1A1]' },
};

/**
 * Given the `tests` array of an FMSAssessmentResponse, compute a status per
 * body region:
 *  - 'high'     any relevant test flagged pain
 *  - 'moderate' no pain, but a relevant test scored <=1 or flagged asymmetry
 *  - 'low'      all relevant tests scored >=2, no pain, no asymmetry
 *  - 'unknown'  none of the relevant tests were part of this assessment
 */
export function computeRegionStatuses(tests) {
  const byTestId = {};
  (tests || []).forEach(t => { byTestId[t.test_id] = t; });

  const statuses = {};
  for (const region of Object.keys(REGION_TEST_MAP)) {
    const relevant = REGION_TEST_MAP[region]
      .map(testId => byTestId[testId])
      .filter(Boolean);

    if (relevant.length === 0) {
      statuses[region] = { status: 'unknown', tests: [] };
      continue;
    }

    let status = 'low';
    if (relevant.some(t => t.pain_flag)) {
      status = 'high';
    } else if (relevant.some(t => t.score <= 1 || t.asymmetry_flag)) {
      status = 'moderate';
    }
    statuses[region] = { status, tests: relevant };
  }
  return statuses;
}
